import React, { useState } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Contact = () => {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" });
  };

  const validateForm = () => {
    const { first_name, last_name, email, message } = formData;
    const newErrors = {};

    if (!first_name.trim()) {
      newErrors.first_name = "First name is required*";
    }
    if (!last_name.trim()) {
      newErrors.last_name = "Last name is required*";
    }
    if (!email.trim()) {
      newErrors.email = "Email is required*";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Please enter a valid email address*";
    }
    if (!message.trim()) {
      newErrors.message = "Message is required*";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    try {
      // Same origin as this site: Traefik routes /api to the Express backend
      // and strips the prefix, so "/app_api/..." is the backend's own route.
      const apiEndpoint = `${process.env.REACT_APP_API_URL}/app_api/user/contact-us`;


      const response = await axios.post(apiEndpoint, formData);

      if (response.status === 200) {
        toast.success(response.data.message || "Message sent successfully!");
        setFormData({
          first_name: "",
          last_name: "",
          email: "",
          message: "",
        });
      }
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error(
        error.response?.data?.message ||
        "Failed to send message. Please try again later."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="px-8 lg:px-32 xl:px-52">
        <h2 className="text-center text-2xl lg:text-3xl font-bold mb-5 mt-10">
          Contact Support
        </h2>
        <div className="container mx-auto px-5 py-8">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:grid-cols-2">
              <div>
                <label
                  className="text-sm text-gray-700 font-semibold mb-2 flex items-start"
                  htmlFor="first_name"
                >
                  First Name*
                </label>
                <input
                  className="appearance-none w-full border border-gray-400 shadow-sm shadow-gray-300 rounded py-3 px-4 leading-tight focus:outline-none placeholder-small"
                  id="first_name"
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  placeholder="Enter First Name"
                />
                {errors.first_name && (
                  <p className="text-red-500 text-sm text-left">
                    {errors.first_name}
                  </p>
                )}
              </div>
              <div>
                <label
                  className="text-sm text-gray-700 font-semibold mb-2 flex items-start"
                  htmlFor="last_name"
                >
                  Last Name*
                </label>
                <input
                  className="appearance-none w-full border border-gray-400 shadow-sm shadow-gray-300 rounded py-3 px-4 leading-tight focus:outline-none placeholder-small"
                  id="last_name"
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  placeholder="Enter Last Name"
                />
                {errors.last_name && (
                  <p className="text-red-500 text-sm text-left">
                    {errors.last_name}
                  </p>
                )}
              </div>
            </div>
            <div>
              <label
                className="text-sm text-gray-700 font-semibold mb-2 flex items-start"
                htmlFor="email"
              >
                Email*
              </label>
              <input
                className="appearance-none w-full border border-gray-400 shadow-sm shadow-gray-300 rounded py-3 px-4 leading-tight focus:outline-none placeholder-small"
                id="email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter Email"
              />
              {errors.email && (
                <p className="text-red-500 text-sm text-left">{errors.email}</p>
              )}
            </div>
            <div>
              <label
                className="text-sm text-gray-700 font-semibold mb-2 flex items-start"
                htmlFor="message"
              >
                Message*
              </label>
              <textarea
                className="appearance-none w-full border border-gray-400 shadow-sm shadow-gray-300 rounded py-3 px-4 leading-tight focus:outline-none h-32"
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Enter your message"
              />
              {errors.message && (
                <p className="text-red-500 text-sm text-left">
                  {errors.message}
                </p>
              )}
            </div>
            <div className="flex justify-center mt-4">
              <button
                className={`text-white bg-[#2DBC78] focus:outline-none hover:bg-[#484647] font-medium rounded-lg text-[15px] px-10 py-2.5 text-center ${loading ? "cursor-not-allowed opacity-70" : ""}`}
                type="submit"
                disabled={loading}
              >
                <div className="flex justify-center items-center w-full">
                  {loading ? "Sending..." : "Send Now"}
                  {loading && (
                    <svg
                      className="animate-spin h-5 w-5 ml-2 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 2.042.784 3.9 2.071 5.291l1.929-1.93z"
                      ></path>
                    </svg>
                  )}
                </div>
              </button>
            </div>

            <div className="flex flex-col items-center mt-4">
              <p className="text-sm text-gray-600 mb-2">
                If you have difficulties submitting this form, please mail us at
                <a
                  href="mailto:support@sstechstudio.com"
                  className="text-blue-500 underline ml-1"
                >
                support@sstechstudio.com
                </a>
              </p>

            </div>
          </form>
        </div>
      </div>
      <ToastContainer />
    </>
  );
};

export default Contact;
